import { useNavigate } from "react-router-dom";

import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Sparkles, CheckCircle2, Zap, Loader2, Plus, Lightbulb, Landmark } from "lucide-react";
import { toast } from "sonner";

import { endPointSubscriptionCreate } from "modules/main/services/endpoints";
import { useAuth } from "modules/authentication/hooks/use-auth";
import { formatRut, validateRut } from "utils/rut";

/**
 * Props for {@link AcquirePlusDialog}.
 */
export interface AcquirePlusDialogProps {
    /** Controls dialog visibility. */
    open: boolean;
    /** Called when the dialog requests an open-state change (e.g. X button or overlay click). */
    onOpenChange: (open: boolean) => void;
}

/**
 * Internal type for subscription creation API response.
 *
 * Backend returns two mutually exclusive shapes:
 * - Normal flow: { url, token } — redirect popup to Flow payment form
 * - Already active: { already_active: true, redirect } — navigate to already-active page
 */
type SubscriptionCreateResponse =
    | { url: string; token: string; already_active?: never; redirect?: never }
    | { already_active: true; redirect: string; url?: never; token?: never };

const BENEFITS = [
    "Optimiza compras de cualquier monto",
    "Crea carritos ilimitados",
    "Organiza tus compras en listas ilimitadas",
    "Prueba las novedades antes que nadie",
];

/**
 * Controlled upsell dialog displayed to non-Plus users when their cart total
import { Badge } from "@/components/ui/badge";
 * exceeds the $300.000 CLP optimization threshold.
 *
 * Executes the full subscription creation flow internally when the CTA is clicked:
 * - Opens a blank popup immediately (preserves user gesture for payment redirect)
 * - Calls /api/subscription/create backend endpoint
 * - Shows loading state while awaiting response
 * - Redirects popup to payment_url on success
 * - Shows error toast and resets on failure
 *
 * @param open - Whether the dialog is currently visible.
 * @param onOpenChange - Callback to update the open state.
 */
export const AcquirePlusDialog = ({
    open,
    onOpenChange,
}: AcquirePlusDialogProps): JSX.Element => {
    const [isConnecting, setIsConnecting] = useState(false);
    const [isWaitingConfirmation, setIsWaitingConfirmation] = useState(false);
    const [rut, setRut] = useState("");
    const [isValidatingRut, setIsValidatingRut] = useState(false);
    const { refreshSubscriptionStatus } = useAuth();
    const navigate = useNavigate();

    // Listen for SUBSCRIPTION_OK message from popup windows
    useEffect(() => {
        const handleMessage = async (event: MessageEvent) => {
            if (event.data?.type === "SUBSCRIPTION_OK") {
                await refreshSubscriptionStatus();
                setIsWaitingConfirmation(false);
                onOpenChange(false);
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [refreshSubscriptionStatus, onOpenChange]);

    const [rutValidation, setRutValidation] = useState<"idle" | "valid" | "invalid">("idle");

    const handleValidateRut = async () => {
        const isValid = validateRut(rut);
        if (!isValid) {
            setRutValidation("invalid");
            return;
        }
        setIsValidatingRut(true);
        setRutValidation("idle");
        // Simulated backend check — replace with actual endpoint when available
        await new Promise((resolve) => setTimeout(resolve, 1200));
        setIsValidatingRut(false);
        setRutValidation("valid");
        // toast.success("");
    };

    const handleConnect = async () => {
        // Prevent double-click
        if (isConnecting) {
            return;
        }

        // Open popup immediately to preserve user gesture
        const popup = window.open("", "_blank");

        if (!popup) {
            toast.error("Por favor permite ventanas emergentes para continuar.");
            return;
        }

        setIsConnecting(true);

        try {
            const response = await fetch(endPointSubscriptionCreate, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage =
                    errorData.detail || "Error al conectar con el canal de pago.";
                throw new Error(errorMessage);
            }

            const data: SubscriptionCreateResponse = await response.json();

            // Check if user already has active subscription
            if ("redirect" in data ) {
                // Close popup and redirect
                popup.close();
                navigate(data.redirect??"/store");
                onOpenChange(false);
                return;
            }

            // Normal flow: redirect popup to Flow payment URL with token
            popup.location.href = `${data.url}?token=${data.token}`;

            // Keep dialog open waiting for confirmation from popup
            setIsConnecting(false);
            setIsWaitingConfirmation(true);
        } catch (error) {
            // Close popup on failure
            popup.close();

            // Show error toast
            const message =
                error instanceof Error ? error.message : "Error de conexión";
            toast.error(message);
        } finally {
            setIsConnecting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(value) => {
                // Prevent closing dialog while waiting for payment confirmation
                if (isWaitingConfirmation && !value) return;
                onOpenChange(value);
            }}
        >
            <DialogContent className="sm:max-w-md pt-8">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-extrabold leading-tight text-left">
                        <span
                            className={`font-bold russo-one-regular logo-text text-2xl text-foreground`}
                        >
                            Carriapp
                        </span>
                        <span className="inline-flex h-4 ml-2 items-center rounded-full border border-border/50 bg-background px-1.5 text-[20px] font-semibold leading-none">
                            <span className="font-semibold bg-[linear-gradient(90deg,#F9802D_0%,#F9802D_42%,#5CA273_100%)] bg-[length:220%_220%] bg-clip-text text-transparent animate-[ai-border-flow_8s_cubic-bezier(0.4,0,0.2,1)_infinite]">
                            +Plus
                            </span>
                        </span> <span>, sigue ahorrando todo el mes</span>
                    </DialogTitle>
                    <DialogDescription className="text-base text-muted-foreground mt-1">
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 my-4">
                    {BENEFITS.map((benefit) => (
                        <div key={benefit} className="flex items-start gap-4">
                            <Plus
                                size={20}
                                className="text-primary mt-0.5 shrink-0"
                            />
                            <span className="text-sm font-semibold
                            ">{benefit}</span>
                        </div>
                    ))}
                </div>

                <div className="bg-primary/10 rounded-lg p-3 text-sm font-medium text-center text-primary">
                    <Lightbulb size={16} className="inline mr-2" />
                    Una sola optimización ya te paga la suscripción
                </div>
                <div>
                    <span className="text-3xl font-bold text-foreground">$3.990</span>
                    <span className="text-sm font-bold text-muted-foreground"> / mes</span>
                </div>
                <div className="text-center">
                    <span className="text-xl font-bold text-foreground">Pruebalo gratis por 30 días</span>
                </div>
                <DialogFooter className="flex-col gap-2 mt-0 sm:flex-col">
                    {isWaitingConfirmation ? (
                        <div className="bg-primary/10 rounded-lg p-4 text-sm font-medium text-center text-primary">
                            <Loader2 size={16} className="inline mr-2 animate-spin" />
                            Esperando confirmación de registro...
                        </div>
                    ) : (
                        <>
                            <Button
                                variant="default"
                                size="lg"
                                className="text-base px-4"
                                onClick={handleConnect}
                                disabled={isConnecting}
                            >
                                {isConnecting ? (
                                    <>
                                        <Loader2 size={16} className="mr-2 animate-spin" />
                                        Conectando con canal de pago...
                                    </>
                                ) : (
                                    <>
                                        Probar 30 días gratis
                                    </>
                                )}
                            </Button>
                            <DialogClose asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="w-full text-muted-foreground"
                                    disabled={isConnecting}
                                >
                                    Continuar sin Carriapp Plus
                                </Button>
                            </DialogClose>
                        </>
                    )}
                   <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="company-benefits" className="border-border/50">
                          <AccordionTrigger className="text-sm font-medium text-muted-foreground hover:text-foreground py-3">
                           <span className="flex items-center gap-2"><Landmark /> ¿Tienes beneficios de tu empresa o caja?</span>
                          </AccordionTrigger>
                          <AccordionContent className="space-y-3 pb-4">
                              <p className="text-xs text-muted-foreground">
                                  Ingresa tu RUT para verificar si tienes convenios disponibles a través de tu empresa o caja de compensación.
                              </p>
                               {rutValidation === "invalid" && (
                                   <p className="text-xs text-destructive font-medium">
                                       RUT inválido. Verifica el formato y dígito verificador.
                                   </p>
                               )}
                               {rutValidation === "valid" && (
                                   <p className="text-xs text-green-600 font-medium flex items-center gap-1">
                                       <CheckCircle2 size={12} /> RUT válido
                                   </p>
                               )}
                               <div className="flex gap-2">
                                   <Input
                                       placeholder="Ej: 12.345.678-9"
                                       value={rut}
                                       onChange={(e) => {
                                           setRut(formatRut(e.target.value));
                                           setRutValidation("idle");
                                       }}
                                       className={`flex-1 ${rutValidation === "invalid" ? "border-destructive ring-destructive/20" : ""} ${rutValidation === "valid" ? "border-green-500 ring-green-500/20" : ""}`}
                                   />
                                  <Button
                                      variant="outline"
                                      size="sm"
                                      disabled={isValidatingRut || !rut.trim()}
                                      onClick={handleValidateRut}
                                  >
                                      {isValidatingRut ? (
                                          <Loader2 size={14} className="animate-spin" />
                                      ) : (
                                          "Validar"
                                      )}
                                  </Button>
                              </div>
                          </AccordionContent>
                      </AccordionItem>
                  </Accordion>                   
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
